/**
 * Welle 1 (Cockpit-Intelligenz) W1-C — client wrapper for the read-only
 * drill-down callable `getCockpitDocumentDetail`.
 *
 * Reference: architecture-cockpit-intelligence.md §Interfaces (Backend —
 * Drill-down). Thin httpsCallable binding (same pattern as {@link
 * ./advisorCockpitApi}) — ALL hard checks (Loop-B-Beta + Advisor-Scope,
 * cross-tenant read of `supplier_iban_history`) live server-side
 * (`functions/src/moat/cockpitDocumentDetail.ts`). READ-ONLY — no write path
 * on this module.
 *
 * DB→FE mapping: `ibanHistory.lastSeenAt` and every `resolutionTrail` entry's
 * `createdAt` are Firestore `Timestamp`s on the backend; both are parsed via
 * the SAME defensive {@link parseTimestampMillis} helper `cockpitResolutionApi`
 * uses (kein Copy-Paste, Golden Rule "Shared Mapper-Funktionen für DB→API").
 * The `resolution` embedded on each finding is narrow-mapped the same way an
 * active-worklist `CockpitFinding.resolution` is (architecture §Datenmodelle
 * #2) — malformed entries fall back to `null` (treated as "no resolution
 * known yet"), never fabricated.
 *
 * @module api/cockpitDocumentDetailApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import type { CockpitAmpel } from './advisorCockpitApi';
import {
  parseTimestampMillis,
  mapRawResolution,
  type CheckResolution,
  type CheckResolutionRole,
} from './cockpitResolutionApi';

const RESOLUTION_TYPES_ACTIVE: readonly ('resolved' | 'ignored')[] = ['resolved', 'ignored'];
const RESOLUTION_ROLES: readonly CheckResolutionRole[] = ['owner', 'advisor'];

/** Current resolution state embedded on a finding; `null` ⇒ still active. */
export interface CockpitFindingResolution {
  resolutionType: 'resolved' | 'ignored';
  resolvedByRole: CheckResolutionRole;
  note: string;
  /** `resolvedAt` (Firestore Timestamp) → FE millis. */
  resolvedAt: number;
}

/** One finding of the drilled-down document, with structured evidence. */
export interface CockpitDocumentDetailFinding {
  checkId: string;
  ampel: CockpitAmpel;
  code: string;
  /** Structured facts only (e.g. `previousIban`/`currentIban`/`missingFields`) — never legal-claim free text. */
  evidence: Record<string, unknown>;
  fingerprint: string;
  resolution: CockpitFindingResolution | null;
}

/** Supplier-IBAN history for the document's supplier, or `null` when unavailable. */
export interface CockpitIbanHistory {
  previousIban: string | null;
  lastIban: string | null;
  /** `supplier_iban_history.lastSeenAt` (Firestore Timestamp) → FE millis, or null. */
  lastSeenAt: number | null;
}

/**
 * Welle 2 (W2-1c) — BLZ→Bank/BIC resolution for a DE IBAN, resolved server-side
 * (In-Memory lookup against the committed Bundesbank BLZ asset, no extra
 * Firestore read). `null` for non-DE IBANs or an unresolvable BLZ.
 */
export interface CockpitBankInfo {
  blz: string;
  bankName: string;
  bic: string;
}

/** Result of {@link getCockpitDocumentDetail}. */
export interface CockpitDocumentDetailResult {
  docId: string;
  supplierName: string;
  invoiceNumber: string;
  documentDate: string;
  grossTotal: number | null;
  currency: string;
  findings: CockpitDocumentDetailFinding[];
  ibanHistory: CockpitIbanHistory | null;
  /** Full audit trail of this document (all findings, all events), oldest first. */
  resolutionTrail: CheckResolution[];
  /** Welle 2 (W2-1c) — bank/BIC display info for a DE IBAN, or `null`. */
  bankInfo: CockpitBankInfo | null;
}

/**
 * Runtime-narrows a raw embedded `resolution` value into a {@link
 * CockpitFindingResolution}. Fail-closed: an unparseable/unknown-enum value
 * maps to `null` (treated as "no resolution known"), never fabricated.
 *
 * @param raw - the raw `resolution` field of one finding.
 * @returns the typed resolution, or `null`.
 */
function mapFindingResolution(raw: unknown): CockpitFindingResolution | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const resolutionType = r.resolutionType as unknown;
  if (typeof resolutionType !== 'string' || !RESOLUTION_TYPES_ACTIVE.includes(resolutionType as 'resolved' | 'ignored')) {
    return null;
  }
  const resolvedByRole = r.resolvedByRole as unknown;
  if (typeof resolvedByRole !== 'string' || !RESOLUTION_ROLES.includes(resolvedByRole as CheckResolutionRole)) {
    return null;
  }
  const resolvedAt = parseTimestampMillis(r.resolvedAt);
  if (resolvedAt === null) return null;

  return {
    resolutionType: resolutionType as 'resolved' | 'ignored',
    resolvedByRole: resolvedByRole as CheckResolutionRole,
    note: typeof r.note === 'string' ? r.note : '',
    resolvedAt,
  };
}

/**
 * Runtime-narrows one raw finding of the drill-down response. Missing/wrong-
 * typed scalars fall back to safe display defaults (empty evidence object,
 * empty code) rather than dropping the whole document detail — unlike the
 * list-level `check_resolutions` mapping, a single malformed finding here must
 * not hide the rest of the detail view.
 *
 * @param raw - the raw finding object.
 * @returns the typed finding, or `null` when the entry is entirely unusable.
 */
function mapDetailFinding(raw: unknown): CockpitDocumentDetailFinding | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  if (typeof f.checkId !== 'string' || typeof f.code !== 'string' || typeof f.fingerprint !== 'string') {
    return null;
  }
  const ampel: CockpitAmpel = f.ampel === 'red' || f.ampel === 'yellow' || f.ampel === 'green' ? f.ampel : 'yellow';
  const evidence: Record<string, unknown> =
    f.evidence && typeof f.evidence === 'object' ? (f.evidence as Record<string, unknown>) : {};

  return {
    checkId: f.checkId,
    ampel,
    code: f.code,
    evidence,
    fingerprint: f.fingerprint,
    resolution: mapFindingResolution(f.resolution),
  };
}

/**
 * Maps the raw `ibanHistory` field of the drill-down response.
 *
 * @param raw - the raw `ibanHistory` value (or null/absent).
 * @returns the typed history, or `null` when absent/unusable.
 */
function mapIbanHistory(raw: unknown): CockpitIbanHistory | null {
  if (!raw || typeof raw !== 'object') return null;
  const h = raw as Record<string, unknown>;
  return {
    previousIban: typeof h.previousIban === 'string' ? h.previousIban : null,
    lastIban: typeof h.lastIban === 'string' ? h.lastIban : null,
    lastSeenAt: parseTimestampMillis(h.lastSeenAt),
  };
}

/**
 * Welle 2 (W2-1c) — maps the raw `bankInfo` field of the drill-down response.
 * Fail-closed: `blz`/`bankName`/`bic` must ALL be non-empty strings, otherwise
 * `null` is returned — never fabricated/partially-filled from a malformed
 * server payload (Golden Rule "fail-closed bei null/NaN-Input").
 *
 * @param raw - the raw `bankInfo` value (or null/absent).
 * @returns the typed bank info, or `null` when absent/unusable.
 */
function mapBankInfo(raw: unknown): CockpitBankInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const b = raw as Record<string, unknown>;
  if (typeof b.blz !== 'string' || b.blz.length === 0) return null;
  if (typeof b.bankName !== 'string' || b.bankName.length === 0) return null;
  if (typeof b.bic !== 'string' || b.bic.length === 0) return null;
  return { blz: b.blz, bankName: b.bankName, bic: b.bic };
}

/**
 * Loads the read-only evidence detail for one inbound document — which
 * invoice/supplier/date it is, structured evidence per finding, the
 * supplier's IBAN history, and the full resolution audit trail. Intended to be
 * called LAZILY on row-expand (never for every worklist row — see
 * architecture §Interfaces "Begründung Drill-down-Callable").
 *
 * @param mandantUid - the mandant's scope uid.
 * @param docId - the inbound document id.
 * @returns the drilled-down detail.
 * @throws Error (normalised) with `code==='permission-denied'` for a foreign
 *   mandant, or on unexpected server failures.
 */
export async function getCockpitDocumentDetail(
  mandantUid: string,
  docId: string,
): Promise<CockpitDocumentDetailResult> {
  const callable = httpsCallable<{ mandantUid: string; docId: string }, Record<string, unknown>>(
    functions,
    'getCockpitDocumentDetail',
  );
  try {
    const res = await callable({ mandantUid, docId });
    const data = res.data ?? {};
    const rawFindings = Array.isArray(data.findings) ? data.findings : [];
    const rawTrail = Array.isArray(data.resolutionTrail) ? data.resolutionTrail : [];

    const findings: CockpitDocumentDetailFinding[] = [];
    for (const raw of rawFindings) {
      const mapped = mapDetailFinding(raw);
      if (mapped) findings.push(mapped);
    }

    return {
      docId: typeof data.docId === 'string' ? data.docId : docId,
      supplierName: typeof data.supplierName === 'string' ? data.supplierName : '',
      invoiceNumber: typeof data.invoiceNumber === 'string' ? data.invoiceNumber : '',
      documentDate: typeof data.documentDate === 'string' ? data.documentDate : '',
      grossTotal: typeof data.grossTotal === 'number' && Number.isFinite(data.grossTotal) ? data.grossTotal : null,
      currency: typeof data.currency === 'string' ? data.currency : '',
      findings,
      ibanHistory: mapIbanHistory(data.ibanHistory),
      resolutionTrail: rawTrail
        .map((raw) => mapRawResolution(raw))
        .filter((r): r is CheckResolution => r !== null),
      bankInfo: mapBankInfo(data.bankInfo),
    };
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server
 * message and exposing the coarse error code (e.g. `permission-denied`)
 * without leaking internals.
 *
 * @param err - the raw callable rejection.
 * @returns a plain Error carrying an optional `code`.
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
